(() => {
  if (window.__cartDrawerV2QuantitySelectorInitialised) {
    return;
  }

  window.__cartDrawerV2QuantitySelectorInitialised = true;

  const selectorRoot = '[data-cart-drawer-v2-quantity-selector]';
  const selectorAction = '[data-cart-drawer-v2-quantity-selector-action]';

  let activeSelector = null;

  const setLoadingState = (selector, isLoading) => {
    if (!selector) {
      return;
    }

    selector.classList.toggle('is-loading', isLoading);

    selector.querySelectorAll(selectorAction).forEach((button) => {
      button.setAttribute('aria-disabled', String(isLoading));

      if (isLoading) {
        button.setAttribute('tabindex', '-1');
      } else {
        button.removeAttribute('tabindex');
      }
    });
  };

  const clearLoadingState = (requestState) => {
    if (!requestState?.info?.cartDrawerV2QuantitySelector) {
      return;
    }

    if (activeSelector?.isConnected) {
      setLoadingState(activeSelector, false);
    }

    activeSelector = null;
  };

  const getCurrentQuantity = (selector) => {
    const quantityFromData = Number(selector.dataset.currentQuantity);

    if (Number.isFinite(quantityFromData)) {
      return quantityFromData;
    }

    const quantityText = selector.querySelector('.cart-drawer-v2__line__quantity-selector__quantity')?.textContent?.trim();
    const quantityFromText = Number(quantityText);

    return Number.isFinite(quantityFromText) ? quantityFromText : null;
  };

  // liquid-ajax-cart assigns its methods asynchronously, after it has resolved the
  // initial cart state, so a click can land before it is usable. Wait for it, then
  // fall back to updating the cart directly. This never navigates: the side cart is
  // the only cart UI, so leaving the page is always the wrong outcome.
  const LIQUID_AJAX_CART_WAIT_MS = 3000;

  const whenCartReady = () => {
    if (window.liquidAjaxCart?.change) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      let settled = false;

      const finish = (isReady) => {
        if (settled) {
          return;
        }

        settled = true;
        clearInterval(pollId);
        clearTimeout(timeoutId);
        document.removeEventListener('liquid-ajax-cart:init', onInit);
        resolve(isReady);
      };

      const onInit = () => finish(Boolean(window.liquidAjaxCart?.change));

      // The init event fires once; poll as well in case it fired before this
      // listener was attached.
      const pollId = setInterval(() => {
        if (window.liquidAjaxCart?.change) {
          finish(true);
        }
      }, 50);

      const timeoutId = setTimeout(() => finish(false), LIQUID_AJAX_CART_WAIT_MS);

      document.addEventListener('liquid-ajax-cart:init', onInit);
    });
  };

  // Liquid bakes the target quantity onto each button as data-quantity. Use that
  // when the selector's own attributes are missing or unparseable.
  const readTargetFromButton = (trigger, selector) => {
    const line = Number(selector.dataset.lineIndex);
    const quantity = Number(trigger.dataset.quantity);

    if (!Number.isInteger(line) || line < 1 || !Number.isInteger(quantity) || quantity < 0) {
      return null;
    }

    return { line, quantity };
  };

  const shopRoot = () => window.Shopify?.routes?.root || '/';

  const changeLineDirectly = async (line, quantity) => {
    const response = await fetch(`${shopRoot()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });

    if (!response.ok) {
      throw new Error(`cart/change.js responded ${response.status}`);
    }

    return response.json();
  };

  const updateCartCount = (cart) => {
    const wrapper = document.querySelector('#cart-icon-bubble [data-ajax-cart-section]')
      || document.querySelector('#cart-icon-bubble');

    if (!wrapper) {
      return;
    }

    const itemCount = Number(cart?.item_count) || 0;
    let bubble = wrapper.querySelector('.cart-count-bubble');

    if (!itemCount) {
      bubble?.remove();
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      wrapper.appendChild(bubble);
    }

    bubble.innerHTML = itemCount < 100
      ? `<span aria-hidden="true">${itemCount}</span><span class="visually-hidden">${itemCount}</span>`
      : `<span class="visually-hidden">${itemCount}</span>`;
  };

  const refreshDrawer = async (cart) => {
    updateCartCount(cart);

    const destination = document.querySelector('#CartDrawerV2 .cart-drawer-v2__inner');

    if (!destination) {
      return;
    }

    const response = await fetch(`${shopRoot()}?section_id=cart-drawer`);

    if (!response.ok) {
      return;
    }

    const source = new DOMParser()
      .parseFromString(await response.text(), 'text/html')
      .querySelector('#CartDrawerV2 .cart-drawer-v2__inner');

    if (!source) {
      return;
    }

    window.__Theme?.unmountComponents?.(destination);
    destination.innerHTML = source.innerHTML;
    window.__Theme?.mountComponents?.(destination);

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  };

  const resolveTarget = (trigger, selector) => {
    const line = Number(selector.dataset.lineIndex);
    const currentQuantity = getCurrentQuantity(selector);

    if (Number.isInteger(line) && line >= 1 && currentQuantity !== null) {
      const delta = trigger.dataset.cartDrawerV2QuantitySelectorAction === 'increase' ? 1 : -1;

      return { line, quantity: Math.max(currentQuantity + delta, 0) };
    }

    return readTargetFromButton(trigger, selector);
  };

  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest(selectorAction);

    if (!trigger) {
      return;
    }

    const selector = trigger.closest(selectorRoot);

    if (!selector) {
      return;
    }

    const target = resolveTarget(trigger, selector);

    if (!target) {
      return;
    }

    // Past this point the click is ours: never let it navigate.
    event.preventDefault();

    if (window.liquidAjaxCart?.processing) {
      return;
    }

    const { line, quantity: nextQuantity } = target;

    activeSelector = selector;
    setLoadingState(selector, true);

    const isReady = await whenCartReady();

    // The line may have moved while we waited, so re-read it before sending.
    const currentLine = Number(selector.dataset.lineIndex);
    const targetLine = Number.isInteger(currentLine) && currentLine > 0 ? currentLine : line;

    if (isReady) {
      window.liquidAjaxCart.change(
        {
          line: targetLine,
          quantity: nextQuantity,
        },
        {
          info: {
            cartDrawerV2QuantitySelector: true,
          },
        }
      );

      return;
    }

    // liquid-ajax-cart never came up. Update the cart directly and re-render the
    // drawer rather than navigating — leaving the page is never the right outcome.
    console.error('[CartDrawerV2QuantitySelector] liquid-ajax-cart unavailable, updating the cart directly');

    try {
      const cart = await changeLineDirectly(targetLine, nextQuantity);
      await refreshDrawer(cart);
    } catch (error) {
      console.error('[CartDrawerV2QuantitySelector] Direct cart update failed', error);
    } finally {
      setLoadingState(selector, false);
      activeSelector = null;
    }
  });

  document.addEventListener('liquid-ajax-cart:request-end', (event) => {
    clearLoadingState(event.detail?.requestState);
  });
})();
